<script setup lang="ts">
import { ref, onMounted } from "vue";
import { helpTL } from "../../generated/tl";
import { useFlash } from "mu-vue-utils/Composables/flash";
import { formatTlError } from "../../services/tlError";
import type { WMModal } from "../../composables/WM";
import Loader from "../Loader.vue";
import HelpMenuTree from "./HelpMenuTree.vue";
import type { HelpIndexNode } from "../../types/help";

defineProps<{ modal: WMModal }>();

const { addFlash } = useFlash();

const content = ref<HelpIndexNode[]>([]);
const selectedFile = ref<string | null>(null);
const html = ref("");
const loadingIndex = ref(true);
const loadingPage = ref(false);
const pageCache = new Map<string, string>();

const selectFile = async (file: string) => {
	selectedFile.value = file;
	const cached = pageCache.get(file);
	if (cached !== undefined) {
		html.value = cached;
		return;
	}
	loadingPage.value = true;
	try {
		const rendered = await helpTL.renderHelpPage(file);
		pageCache.set(file, rendered);
		html.value = rendered;
	} catch (e) {
		addFlash(formatTlError(e), "danger");
	} finally {
		loadingPage.value = false;
	}
};

// Markdown odkazy mezi stránkami nápovědy (např. index.md -> [Co všechno umím](skils.md)) se
// vykreslí jako obyčejné <a href="skils.md">, které by prohlížeč zkusil navigovat pryč ze SPA —
// zachytíme klik na cokoliv končící ".md" (bez query/hash) a přepneme stránku stejnou cestou jako
// klik v HelpMenuTree.vue. Jiné odkazy (http(s), mailto, kotvy bez .md) projdou beze změny.
const onContentClick = (event: MouseEvent) => {
	const anchor = (event.target as HTMLElement).closest("a");
	if (!anchor) return;
	const href = anchor.getAttribute("href");
	if (!href) return;
	const path = href.split(/[?#]/)[0];
	if (!path.endsWith(".md")) return;

	event.preventDefault();
	selectFile(path);
};

onMounted(async () => {
	try {
		const index = await helpTL.getHelpIndex();
		content.value = index.content;
		// index.json řídí pořadí i výchozí stránku — otevírá se první položka v pořadí, jak ji autor
		// nápovědy zapsal (typicky "Novinky a změny").
		const first = content.value[0];
		if (first) await selectFile(first.file);
	} catch (e) {
		addFlash(formatTlError(e), "danger");
	} finally {
		loadingIndex.value = false;
	}
});
</script>

<template>
	<div class="help-panel">
		<Loader v-if="loadingIndex" />
		<div v-else class="help-panel__layout">
			<nav class="help-panel__menu">
				<HelpMenuTree :content="content" :selected-file="selectedFile" @select="selectFile" />
			</nav>
			<div class="help-panel__content" @click="onContentClick">
				<Loader v-if="loadingPage" />
				<div v-else v-html="html" />
			</div>
		</div>
	</div>
</template>
